import React from 'react';
import { DollarSign, CreditCard, AlertCircle } from 'lucide-react';
import { BillingSummary, formatCurrency } from './billingTypes';

interface BillingSummaryCardsProps {
  summary: BillingSummary;
  currency?: string;
  loading?: boolean;
}

const BillingSummaryCards: React.FC<BillingSummaryCardsProps> = ({ 
  summary, 
  currency = 'USD',
  loading = false 
}) => {
const cards = [
    {
      label: 'Total Paid',
      value: summary.total_paid,
      icon: DollarSign,
      color: 'var(--v2-success)',
      bg: 'var(--v2-success-bg)',
    },
    {
      label: 'Credits Available',
      value: summary.credits_available || 0,
      icon: CreditCard,
      color: '#1e40af',
      bg: '#dbeafe',
    },
{
      label: 'Outstanding',
      value: summary.outstanding,
      icon: AlertCircle,
      color: summary.outstanding > 0 ? 'var(--v2-error)' : 'var(--v2-text-secondary)',
      bg: summary.outstanding > 0 ? 'var(--v2-error-bg)' : 'var(--v2-surface-alt)',
      urgent: summary.outstanding > 0,
    },
  ];

const getCardStyle = (urgent?: boolean): React.CSSProperties => ({
    flex: '1 1 200px',
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: urgent ? '2px solid var(--v2-error)' : '1px solid var(--v2-border)',
    padding: urgent ? '19px' : '20px', // Compensate for thicker border
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: urgent ? '0 0 0 3px var(--v2-error-bg)' : 'none',
  });

  const iconContainerStyle = (bg: string): React.CSSProperties => ({
    width: '48px',
    height: '48px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  });

  const skeletonStyle: React.CSSProperties = {
    height: '28px',
    width: '80px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-sm)',
    animation: 'pulse 1.5s ease-in-out infinite',
  };

  return (
    <>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
<div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {cards.map((card) => (
          <div key={card.label} style={getCardStyle((card as any).urgent)}>
            <div style={iconContainerStyle(card.bg)}>
              <card.icon size={24} strokeWidth={1.5} color={card.color} />
            </div>
            <div>
              <div style={{ 
                fontSize: '0.75rem', 
                fontWeight: 600, 
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '4px'
              }}>
                {card.label}
              </div>
              {loading ? (
                <div style={skeletonStyle} />
              ) : (
                <div style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 700, 
                  color: 'var(--v2-text)',
                  letterSpacing: '-0.02em'
                }}>
                  {formatCurrency(card.value, currency)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default BillingSummaryCards;
